import React, { useEffect, useState } from "react";
import NotebookPageHeading from "organisms/notebook-page-heading/notebook-page-heading";
import NotebookCallToAction from "./notebook-call-to-action";
import Pages from "./pages/pages";
import LocalStorageKey from "utilities/enumerations/local-storage-keys";
import { LocalStorageUtils } from "utilities/local-storage-utils";
import useCurrentNotebook from "utilities/hooks/domain/notebooks/use-current-notebook";
import { useSortPages } from "utilities/hooks/domain/notebooks/pages/use-sort-pages";
import { CollectionUtils } from "@rsm-hcd/javascript-core";
import { ToastManager } from "utilities/toast/toast-manager";
import { useLocalization } from "utilities/hooks/use-localization";
import PageRecord from "models/view-models/page-record";
import useSetCurrentPage from "utilities/hooks/domain/notebooks/pages/use-set-current-page";
import NotebookPageCreateModal from "organisms/modals/notebook-page-create-modal/notebook-page-create-modal";
import useNotebookActions from "pages/notebooks/use-notebook-actions";
import NotebookShareModal from "organisms/modals/notebook-share-modal/notebook-share-modal";
import useNotebookActionPermission, {
    NotebookAction,
} from "./use-notebook-action-permissions";

const CSS_CLASS_NAME = "c-my-pages-display";

const MyPagesDisplay = () => {
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
    const [callToActionDismissed, setCallToActionDismissed] =
        useState<boolean>(false);
    const { t } = useLocalization();

    const { currentNotebook, updateCurrentNotebook } = useCurrentNotebook();
    useSetCurrentPage({ pageId: null });
    const { can } = useNotebookActionPermission(currentNotebook);

    const {
        clearErrors: clearSortPageErrors,
        errors: sortPageErrors,
        onPagesReordered,
        sortedPages,
    } = useSortPages(currentNotebook?.pages ?? []);

    const { showShareModal, setShowShareModal } =
        useNotebookActions(currentNotebook);

    useEffect(() => {
        const callToActionDismissed = LocalStorageUtils.get(
            LocalStorageKey.NotebookCallToActionDismissed
        );
        setCallToActionDismissed(callToActionDismissed === true);
    }, []);

    useEffect(() => {
        if (CollectionUtils.hasValues(sortPageErrors)) {
            ToastManager.error(
                t("errors-actionResource", {
                    action: t("moving"),
                    resource: t("block"),
                })
            );
            clearSortPageErrors();
        }
    }, [clearSortPageErrors, sortPageErrors, t]);

    const handleDismiss = () => {
        LocalStorageUtils.set(
            LocalStorageKey.NotebookCallToActionDismissed,
            true
        );
        setCallToActionDismissed(true);
    };

    const handlePagesReordered = (reorderedPages: PageRecord[]): void => {
        onPagesReordered(reorderedPages);

        updateCurrentNotebook({ pages: reorderedPages });
    };
    return (
        <section className={CSS_CLASS_NAME}>
            <div className={`${CSS_CLASS_NAME}__heading`}>
                <NotebookPageHeading
                    onCreatePageClick={() => setShowCreateModal(true)}
                    onSharePageClick={() => setShowShareModal(true)}
                />
            </div>
            {!callToActionDismissed && (
                <div className={`${CSS_CLASS_NAME}__call-to-action`}>
                    <NotebookCallToAction
                        onCreatePageClick={() => setShowCreateModal(true)}
                        onDismiss={handleDismiss}
                    />
                </div>
            )}
            <div className={`${CSS_CLASS_NAME}__pages`}>
                <Pages
                    onCreatePageClick={() => setShowCreateModal(true)}
                    onPagesReordered={handlePagesReordered}
                    pages={sortedPages}
                    showAddPageButton={can(NotebookAction.CreatePage)}
                />
            </div>
            <NotebookPageCreateModal
                isVisible={showCreateModal}
                closeDialog={() => setShowCreateModal(false)}
                notebookId={Number(currentNotebook?.id)}
            />
            {showShareModal && currentNotebook && (
                <NotebookShareModal
                    notebook={currentNotebook}
                    isVisible={showShareModal}
                    closeDialog={() => setShowShareModal(false)}
                />
            )}
        </section>
    );
};

export default MyPagesDisplay;
